import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X, Mail, Phone, Globe, MapPin, ExternalLink } from "lucide-react";
import DOMPurify from "dompurify";

const ArticleMedia = ({ url, type, caption }) => {
  const media = type === "video" ? (
    <video src={url} controls muted autoPlay loop playsInline className="w-full max-h-[400px] object-contain bg-stone-100 rounded-lg" />
  ) : (
    <img src={url} alt={caption || ""} className="w-full max-h-[400px] object-contain bg-stone-100 rounded-lg" loading="lazy" />
  );
  return (
    <figure className="my-6">
      {media}
      {caption && <figcaption className="text-xs text-stone-500 text-center mt-2">{caption}</figcaption>}
    </figure>
  );
};

const ArticleBlock = ({ block }) => {
  if (block.type === "paragraph") {
    return (
      <p
        className="text-stone-700 leading-relaxed my-4 prose prose-stone max-w-none"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(block.html || "") }}
      />
    );
  }
  if (block.type === "media" && block.media_url) {
    return <ArticleMedia url={block.media_url} type={block.media_type} caption={block.caption} />;
  }
  return null;
};

const ContactSection = ({ contact }) => {
  if (!contact) return null;
  const items = [
    contact.phone && { icon: Phone, label: contact.phone, href: `tel:${contact.phone}` },
    contact.email && { icon: Mail, label: contact.email, href: `mailto:${contact.email}` },
    contact.website && { icon: Globe, label: contact.website, href: contact.website.startsWith("http") ? contact.website : `https://${contact.website}` },
    contact.address && { icon: MapPin, label: contact.address, href: null },
  ].filter(Boolean);

  if (!items.length) return null;

  return (
    <div className="mt-8 pt-6 border-t border-stone-200">
      <h3 className="text-sm font-semibold text-stone-900 mb-3 uppercase tracking-wide">Contact</h3>
      <div className="space-y-2">
        {items.map((item, i) => {
          const Icon = item.icon;
          const content = (
            <div className="flex items-center gap-3 text-stone-700 hover:text-[#2D5A3D] transition-colors">
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{item.label}</span>
            </div>
          );
          return item.href ? (
            <a key={i} href={item.href} target={item.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="block">
              {content}
            </a>
          ) : (
            <div key={i}>{content}</div>
          );
        })}
      </div>
    </div>
  );
};

const LinksSection = ({ links }) => {
  if (!links?.length) return null;
  return (
    <div className="mt-6 flex flex-wrap gap-2">
      {links.map((link, i) => (
        <a
          key={i}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#2D5A3D] hover:bg-[#1E3A2F] text-white text-sm rounded-lg transition-colors"
        >
          {link.label}
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      ))}
    </div>
  );
};

/**
 * Drawer that displays the rich article when a banner with click_action="article" is clicked.
 * Centered overlay with scrollable content. Closable via X or ESC.
 */
export function BannerArticleDrawer({ banner, open, onClose }) {
  if (!banner?.click_article) return null;
  const article = banner.click_article;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto p-0 gap-0"
        data-testid="banner-article-drawer"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 bg-white/90 hover:bg-white rounded-full p-2 shadow-md transition-colors"
          aria-label="Close article"
          data-testid="banner-article-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Hero media */}
        {article.hero_media_url && (
          <div className="w-full bg-stone-100">
            {article.hero_media_type === "video" ? (
              <video
                src={article.hero_media_url}
                controls
                autoPlay
                muted
                loop
                playsInline
                className="w-full max-h-[400px] object-cover"
              />
            ) : (
              <img
                src={article.hero_media_url}
                alt={article.title || ""}
                className="w-full max-h-[400px] object-cover"
              />
            )}
          </div>
        )}

        {/* Article body */}
        <div className="px-6 py-8 md:px-10 md:py-10">
          {article.title && (
            <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-6 leading-tight">
              {article.title}
            </h1>
          )}

          {(article.blocks || []).map((block, i) => (
            <ArticleBlock key={i} block={block} />
          ))}

          <LinksSection links={article.links || []} />
          <ContactSection contact={article.contact} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default BannerArticleDrawer;
